import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    MdPerson,
    MdFingerprint,
    MdDevices,
    MdLocationOn,
    MdEventNote,
    MdCheckCircle,
    MdError,
    MdCalendarMonth,
    MdOutlineDataObject,
    MdBadge,
    MdSwapHoriz,
    MdReceiptLong,
} from "react-icons/md";
import { useAuth } from "../../Context/AuthContext";
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from "../../Utils/Permissions";
import { getAuditLogById, getAudit100Logs } from "../../Services/AuditLogService";
import { APPRoutes } from "../../Utils/Route";
import { formatUtcToLocalDateTime } from "../../Utils/DateTime";
import { toast } from "react-toastify";
import ViewEmptyState from "../../Components/Common/ViewEmptyState";
import BackButton from "../../Components/Common/BackButton";

const normalizeAuditLog = (log) => {
    if (!log) return log;
    return {
        ...log,
        Id: log.Id ?? log.id,
        Event: log.Event ?? log.event,
        CreatedOn: log.CreatedOn ?? log.created_on ?? log.Timestamp ?? log.CreatedAt,
        ResourceType: log.ResourceType ?? log.resource_type ?? log.Resource,
        UserName: log.UserName ?? log.user_name ?? log.User,
        RoleName: log.RoleName ?? log.role_name ?? log.ActorType,
        ActorType: log.ActorType ?? log.actor_type,
        ResourceId: log.ResourceId ?? log.resource_id,
        ActorId: log.ActorId ?? log.actor_id,
        Metadata: log.Metadata ?? log.metadata ?? log.metadata_json,
    };
};

const eventLabel = (raw) => raw ? raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

const serializeValue = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "object") return JSON.stringify(val, null, 2);
    return String(val);
};

// Automation runs also log "skipped" and "partial_failure"; neither is an error, so they get the warning tone.
const WARNING_STATUSES = ["skipped", "partial_failure"];

const StatusPill = ({ status }) => {
    const normalized = status?.toLowerCase();
    const ok = normalized === "success";
    const warning = WARNING_STATUSES.includes(normalized);
    const label = status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ") : "—";
    return (
        <span
            className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold"
            style={{
                fontSize: "0.82rem",
                backgroundColor: ok ? "var(--hc-status-success-bg)" : warning ? "var(--hc-status-warning-bg)" : "var(--hc-status-danger-bg)",
                color: ok ? "var(--hc-status-success-text-strong)" : warning ? "var(--hc-status-warning-text)" : "var(--hc-status-danger-text-strong)", }} >
            {ok ? <MdCheckCircle size={15} /> : <MdError size={15} />}
            {label}
        </span>
    );
};

const InfoRow = ({ icon: Icon, label, value, mono = false }) => (
    <div className="d-flex align-items-start gap-3 py-3 border-bottom border-light-subtle">
        <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: 36, height: 36, background: "linear-gradient(135deg, rgba(var(--hc-accent-blue-rgb), 0.08) 0%, rgba(var(--hc-accent-blue-rgb), 0.12) 100%)", color: "var(--hc-accent-blue)", }} >
            <Icon size={16} />
        </span>
        <div className="flex-grow-1 min-w-0">
            <div className="small text-muted mb-1">{label}</div>
            <div className={`fw-medium text-dark text-break ${mono ? "font-monospace" : ""}`} style={{ fontSize: "0.9rem" }} >
                {value || "—"}
            </div>
        </div>
    </div>
);

const ValuePanel = ({ label, value, labelColor, bg, border }) => {
    const serialized = serializeValue(value);
    return (
        <div className="p-3 rounded-4 h-100" style={{ backgroundColor: bg, border: `1px solid ${border}` }}>
            <p className="small fw-semibold mb-2" style={{ color: labelColor }}>{label}</p>
            {serialized ? (
                <pre className="mb-0 text-secondary font-monospace m-0" style={{ fontSize: "0.82rem", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                    {serialized}
                </pre>
            ) : (
                <em className="text-muted" style={{ fontSize: "0.88rem" }}>None</em>
            )}
        </div>
    );
};

const SectionCard = ({ title, icon: Icon, children, accentColor = "var(--hc-accent-teal)" }) => (
    <div className="hc-surface h-100 hc-card-primary overflow-hidden">
        <div className="px-4 pt-4 pb-2 d-flex align-items-center gap-2">
            <span
                className="d-inline-flex align-items-center justify-content-center rounded-3"
                style={{ width: 34, height: 34, backgroundColor: `${accentColor}15`, color: accentColor, }} >
                <Icon size={18} />
            </span>
            <h6 className="fw-bold mb-0" style={{ color: accentColor }}>
                {title}
            </h6>
        </div>
        <div className="px-4 pb-3">{children}</div>
    </div>
);

const ViewSkeleton = () => (
    <div className="container-fluid py-4 hc-page-bg min-vh-100">
        <div className="d-flex gap-2 mb-3">
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 110, borderRadius: 999 }} />
        </div>
        <div className="p-4 mb-4 hc-surface">
            <div className="skeleton mb-3 rounded-3" style={{ height: 28, width: "55%" }} />
            <div className="skeleton mb-2 rounded-2" style={{ height: 18, width: "35%" }} />
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 18, width: "20%" }} />
        </div>
        <div className="row g-4">
            {[1, 2, 3].map((i) => (
                <div className="col-lg-4" key={i}>
                    <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 220, borderRadius: 16 }} />
                </div>
            ))}
            <div className="col-12">
                <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 160, borderRadius: 16 }} />
            </div>
        </div>
    </div>
);

export default function AuditLogView() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken]
    );
    const canViewAuditLogs = permissionHas(userPermissionCodes, PermissionCodes.AUDIT_LOGS_VIEW, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, PermissionCodes.AUDIT_LOGS_LIST, decodedToken);

    const [log, setLog] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    const targetLogId = React.useMemo(() => {
        if (!id || id === 'first' || id === ':id') return null;
        return id;
    }, [id]);

    React.useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                setLoading(true);
                let resolvedId = targetLogId;
                if (!resolvedId) {
                    const listRes = await getAudit100Logs(token);
                    const list = Array.isArray(listRes?.data) ? listRes.data : Array.isArray(listRes) ? listRes : [];
                    if (list.length > 0) {
                        resolvedId = list[0]?.Id ?? list[0]?.id;
                    }
                }

                if (!resolvedId) {
                    setLog(null);
                    return;
                }

                const res = await getAuditLogById(resolvedId, token);
                if (res?.is_success) {
                    setLog(normalizeAuditLog(res.data));
                } else if (res?.is_success === false && res?.status_code === 500) {
                    toast.error("Internal Server Error. Please Contact Support Team.");
                } else {
                    toast.error(res?.message || "Failed to load audit log");
                }
            } catch (err) {
                console.error(err);
                toast.error("An error occurred while loading audit log");
            } finally {
                setLoading(false);
            }
        })();
    }, [token, id, targetLogId]);

    if (!canViewAuditLogs) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-danger">You do not have permission to view audit log entries.</div>
            </div>
        );
    }

    if (loading) return <ViewSkeleton />;

    if (!log) {
        return (
            <ViewEmptyState
                title="No Audit Logs Available"
                description="There are no audit log records available to display."
                icon={MdReceiptLong}
                canViewList={canViewList}
                backRoute={APPRoutes.AUDIT_LOGS}
                backLabel="Back to Audit Logs"
            />
        );
    }

    const meta = log.Metadata || {};
    const createdLocal = formatUtcToLocalDateTime(log.CreatedOn);
    const metaTimestampLocal = formatUtcToLocalDateTime(meta.timestamp);
    const hasChange = meta.old_value || meta.new_value;

    return (
        <div className="container-fluid py-4"> 
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList && (
                    <BackButton onClick={() => navigate(APPRoutes.AUDIT_LOGS)} />
                )}

                <span className="small text-muted">
                    Log ID &nbsp;<strong className="hc-text-primary">#{log.Id}</strong>
                </span>
            </div>
 
            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-8">
                        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                            <StatusPill status={meta.status} />
                            <span
                                className="px-3 py-2 rounded-pill fw-semibold"
                                style={{fontSize: "0.82rem", backgroundColor: "rgba(var(--hc-white-rgb), 0.15)", color: "var(--hc-white)", }} >
                                {log.ResourceType || "—"}
                            </span>
                            <span
                                className="px-3 py-2 rounded-pill"
                                style={{fontSize: "0.82rem", backgroundColor: "rgba(var(--hc-white-rgb), 0.1)", color: "rgba(var(--hc-white-rgb), 0.75)", }} >
                                {log.ActorType || "—"}
                            </span>
                        </div>

                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: "clamp(1.45rem, 2.2vw, 2rem)", lineHeight: 1.2 }} >
                            {eventLabel(log.Event)}
                        </h2>

                        {meta.description && (
                            <p className="mb-0" style={{ color: "rgba(var(--hc-white-rgb), 0.72)", fontSize: "0.95rem" }}>
                                {meta.description}
                            </p>
                        )}
                    </div>

                    <div className="col-lg-4">
                        <div className="p-3 rounded-4 text-center" style={{ backgroundColor: "rgba(var(--hc-white-rgb), 0.08)" }} >
                            <p className="mb-1 small" style={{ color: "rgba(var(--hc-white-rgb), 0.65)" }}>
                                Performed by
                            </p>
                            <p className="fw-bold text-white mb-1" style={{ fontSize: "1.05rem" }}>
                                {log.UserName || "—"}
                            </p>
                            <p className="mb-3 small" style={{ color: "rgba(var(--hc-white-rgb), 0.65)" }}>
                                {log.RoleName || "—"}
                            </p>
                            <hr style={{ borderColor: "rgba(var(--hc-white-rgb), 0.15)" }} />
                            <p className="mb-1 small" style={{ color: "rgba(var(--hc-white-rgb), 0.65)" }}>
                                <MdCalendarMonth size={13} className="me-1" />
                                Recorded at
                            </p>
                            <p className="fw-semibold text-white mb-0" style={{ fontSize: "0.88rem" }}>
                                {createdLocal}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
 
            <div className="row g-4 mb-4"> 
                <div className="col-lg-4">
                    <SectionCard title="Actor Details" icon={MdPerson} accentColor="var(--hc-accent-blue)">
                        <InfoRow icon={MdPerson} label="User Name" value={log.UserName} />
                        <InfoRow icon={MdBadge} label="Role" value={log.RoleName} />
                        <InfoRow icon={MdBadge} label="Actor Type" value={log.ActorType} />
                        <InfoRow icon={MdFingerprint} label="Actor ID" value={log.ActorId} mono />
                    </SectionCard>
                </div>
 
                <div className="col-lg-4">
                    <SectionCard title="Resource Details" icon={MdOutlineDataObject} accentColor="var(--hc-accent-blue)">
                        <InfoRow icon={MdEventNote} label="Event" value={eventLabel(log.Event)} />
                        <InfoRow icon={MdOutlineDataObject} label="Resource Type" value={log.ResourceType} />
                        <InfoRow icon={MdFingerprint} label="Resource ID" value={log.ResourceId} mono />
                        <InfoRow icon={MdCalendarMonth} label="Timestamp" value={metaTimestampLocal} />
                    </SectionCard>
                </div>
 
                <div className="col-lg-4">
                    <SectionCard title="Network & Client" icon={MdDevices} accentColor="var(--hc-accent-blue)">
                        <InfoRow icon={MdLocationOn} label="IP Address" value={meta.ip_address} mono />
                        <InfoRow icon={MdDevices} label="User Agent" value={meta.user_agent} />
                    </SectionCard>
                </div>
            </div>
 
            {hasChange && (
                <div className="hc-surface hc-card-primary p-4 p-lg-5">
                    <div className="d-flex align-items-center gap-2 mb-4">
                        <span
                            className="d-inline-flex align-items-center justify-content-center rounded-3"
                            style={{ width: 34, height: 34, backgroundColor: "rgba(var(--hc-accent-blue-rgb), 0.08)", color: "var(--hc-accent-blue)" }}
                        >
                            <MdSwapHoriz size={18} />
                        </span>
                        <h6 className="fw-bold mb-0" style={{ color: "var(--hc-accent-blue)" }}>
                            Value Change
                        </h6>
                    </div>

                    <div className="row g-4">
                        <div className="col-md-6">
                            <ValuePanel label="Old Value" value={meta.old_value} labelColor="var(--hc-status-danger-text-strong)" bg="var(--hc-value-panel-old-bg)" border="var(--hc-value-panel-old-border)" />
                        </div>
                        <div className="col-md-6">
                            <ValuePanel label="New Value" value={meta.new_value} labelColor="var(--hc-status-success-text-strong)" bg="var(--hc-value-panel-new-bg)" border="var(--hc-value-panel-new-border)" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}